"use client";

import React, { useState, useEffect, useRef } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Icon } from '@iconify/react';
import { AjaxGet } from "@/util/Ajax";
import useAuthInfo from "@/store/auth";
import { useRouter } from 'next/navigation';
import Swal from "sweetalert2";

interface Campaign {
  encId: string;
  encCampaignTypeId: string;
  encUserId: string;
  title: string;
  description: string;
  logoUrl: string;
  createdOn: string;
  budgetLeft: number;
  budget: number;
  isCampaignActive: boolean;
  isCampaignCompleted: boolean;
  encGenreIds: string[];
  platforms: string[];
  participants: number;
  regionName?: string;
  languageName?: string;
}

const platformIcons: { [key: string]: string } = {
  tiktok: "logos:tiktok-icon",
  instagram: "skill-icons:instagram",
  youtube: "logos:youtube-icon",
  facebook: "logos:facebook"
};

const platformColors: { [key: string]: string } = {
  tiktok: "bg-black text-black",
  instagram: "bg-gradient-to-r from-purple-500 to-pink-500 text-black",
  youtube: "bg-red-500 text-black",
  facebook: "bg-blue-600 text-white"
};

const isValidUrl = (url: string | null): boolean => {
  if (!url || url.trim() === "") return false;
  return url.startsWith("/") || url.startsWith("http://") || url.startsWith("https://");
};

const CampaignLogo = ({ logo, title }: { logo: string | null; title: string }) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const shouldUseFallback = !isValidUrl(logo) || imageError;

  return (
    <div className="w-full h-48 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200 dark:border-slate-700">
      <img
        src={shouldUseFallback ? "/assets/1024.png" : logo!}
        alt={shouldUseFallback ? "Eyam Logo" : `${title} Logo`}
        className="max-w-full  object-cover "
        onError={handleImageError}
      />
    </div>
  );
};

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [campaignType, setCampaignType] = useState<"active" | "completed">("active");
  const [isAuthHydrated, setIsAuthHydrated] = useState(false);
  const userData = useAuthInfo((state) => state);
  const router = useRouter();
  const hasShownAuthPrompt = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const hydrateAuth = async () => {
      await useAuthInfo.persist.rehydrate();
      if (isMounted) setIsAuthHydrated(true);
    };

    hydrateAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthHydrated || userData.apiToken || hasShownAuthPrompt.current) return;

    hasShownAuthPrompt.current = true;

    Swal.fire({
      title: "Login required",
      text: "Please login or register to view campaigns.",
      icon: "info",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Login",
      denyButtonText: "Register",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#72C7DA",
      denyButtonColor: "#111827",
      cancelButtonColor: "#6B7280",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        router.push("/login?redirect=/campaigns");
        return;
      }

      if (result.isDenied) {
        router.push("/register");
        return;
      }

      router.push("/");
    });
  }, [isAuthHydrated, router, userData.apiToken]);

  useEffect(() => {
    if (!isAuthHydrated || !userData.apiToken) return;

    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthHydrated, userData.apiToken]);

  const fetchCampaigns = async () => {
    try {
      const response = await AjaxGet(userData, "Campaign/GetAll");
      if (response?.data) {
        setCampaigns(response.data);
      }
    } catch (err) {
      console.error("Failed to load campaigns:", err);
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = selectedPlatform === "all" || 
                           campaign.platforms?.some(p => p.toLowerCase() === selectedPlatform);
    const matchesType = campaignType === "completed"
      ? campaign.isCampaignCompleted
      : campaign.isCampaignActive && !campaign.isCampaignCompleted;
    return matchesSearch && matchesPlatform && matchesType;
  });

  const activeCount = campaigns.filter((campaign) => campaign.isCampaignActive && !campaign.isCampaignCompleted).length;
  const completedCount = campaigns.filter((campaign) => campaign.isCampaignCompleted).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-5 sm:py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex w-full flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {campaignType === "active" ? "Active Campaigns" : "Completed Campaigns"}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {campaignType === "active"
                ? "Browse and join active campaigns to start earning money from your content"
                : "Browse completed campaigns and view their final reports and results"}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
            <Button
              text="Create Campaign"
              className="w-full sm:w-auto min-h-[44px] bg-primary-500 text-white"
              icon="heroicons:plus"
              onClick={() => {
                router.push("/campaigns/create");
              }}
              isLoading={false}
              disabled={false}
              children={null}
              link={null}
              div={false}
            />
            <Button
              text="View Dashboard"
              icon="heroicons:eye"
              className="w-full sm:w-auto min-h-[44px] bg-[#72C7DA] hover:bg-[#5AB0C1] shadow-lg text-black shadow-[#72C7DA]/50 "
              onClick={() => {
                router.push("/campaigns/dashboard");
              }}
              isLoading={false}
              disabled={false}
              children={null}
              link={null}
              div={false}
            />
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="mb-5 rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setCampaignType("active")}
                className={`flex items-center justify-center gap-2 rounded-xl px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold transition ${
                  campaignType === "active"
                    ? "bg-white text-blue-700 shadow-sm dark:bg-slate-900 dark:text-blue-300"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                <Icon icon="heroicons:bolt" className="h-5 w-5" />
                Active
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">{activeCount}</span>
              </button>
              <button
                type="button"
                onClick={() => setCampaignType("completed")}
                className={`flex items-center justify-center gap-2 rounded-xl px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold transition ${
                  campaignType === "completed"
                    ? "bg-white text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                <Icon icon="heroicons:check-circle" className="h-5 w-5" />
                Completed
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">{completedCount}</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Icon 
                  icon="heroicons:magnifying-glass" 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 min-h-[44px] py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                />
              </div>
            </div>

            {/* Platform Filter */}
            <div className="md:w-48">
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="w-full px-4 min-h-[44px] py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              >
                <option value="all">All Platforms</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign, index) => {
            const totalBudget = campaign.budget || 0;
            const budgetLeft = campaign.budgetLeft || 0;
            const normalizedBudgetLeft = Math.max(Math.min(budgetLeft, totalBudget), 0);
            const spentBudget = Math.max(totalBudget - normalizedBudgetLeft, 0);
            const remainingPercentage = totalBudget > 0
              ? Math.min((normalizedBudgetLeft / totalBudget) * 100, 100)
              : 0;

            return (
            <Card key={campaign.encId || index} className="hover:shadow-lg transition-shadow duration-300 shadow">
              <div className="space-y-4">
                {/* Campaign Logo */}
                <CampaignLogo logo={campaign.logoUrl} title={campaign.title} />

                {/* Campaign Info */}
                <div>
                  <h3 className="text-xl break-words font-semibold text-gray-900 dark:text-white mb-2">
                    {campaign.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-2">
                    {campaign.description}
                  </p>
                </div>

                {/* Budget and Pay Rate */}
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Budget</p>
                    <p className="text-lg font-semibold text-green-600">
                      R{(campaign.budget || 0).toLocaleString("en-ZA")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pay Rate</p>
                    <p className="text-lg font-semibold text-blue-600">
                      R20 / 1000 views
                    </p>
                  </div>
                </div>

                {/* Budget Progress */}
                <div>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Budget left</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-500"
                      style={{ width: `${remainingPercentage}%` }}
                    />
                  </div>
                  {/* <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Used: R{spentBudget.toLocaleString("en-ZA")}
                  </p> */}
                </div>

                {/* Platforms */}
                {campaign.platforms && campaign.platforms.length > 0 && (
                  <div>
                    <p className="text-sm text-black dark:text-gray-400 mb-2">Platforms</p>
                    <div className="flex flex-wrap gap-2">
                      {campaign.platforms.map((platform) => {
                        const platformKey = platform.toLowerCase();
                        return (
                          <span
                            key={platform}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${platformColors[platformKey] || "bg-gray-500 text-white"}`}
                          >
                            <Icon icon={platformIcons[platformKey] || "heroicons:globe-alt"} className="w-3 h-3 mr-1" />
                            {platform}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Region and Language */}
                {(campaign.regionName || campaign.languageName) && (
                  <div className="flex flex-wrap gap-2">
                    {campaign.regionName && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        <Icon icon="heroicons:globe-americas" className="w-3 h-3 mr-1" />
                        {campaign.regionName}
                      </span>
                    )}
                    {campaign.languageName && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        <Icon icon="heroicons:language" className="w-3 h-3 mr-1" />
                        {campaign.languageName}
                      </span>
                    )}
                  </div>
                )}

                {/* Participants */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Icon icon="heroicons:users" className="w-4 h-4 mr-1" />
                    {campaign.participants} participants
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    campaign.isCampaignCompleted
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                      : campaign.isCampaignActive 
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                  }`}>
                    {campaign.isCampaignCompleted ? "Completed" : campaign.isCampaignActive ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Action Button */}
                <Button
                  text={campaign.isCampaignCompleted ? "View Report" : "View Details"}
                  className={`w-full text-white ${campaign.isCampaignCompleted ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}
                  icon="heroicons:arrow-right"
                  iconPosition="right"
                  link={`/campaigns/${campaign.encId}`}
                  isLoading={false}
                  disabled={false}
                  children={null}
                  onClick={null}
                  div={false}
                />
              </div>
            </Card>
            );
          })}
        </div>

        {/* No Results */}
        {filteredCampaigns.length === 0 && (
          <Card className="text-center py-12">
            <Icon icon="heroicons:magnifying-glass" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No campaigns found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Try switching to {campaignType === "active" ? "completed" : "active"} campaigns or adjusting your search filters.
            </p>
          </Card>
        )}

        {/* Campaigns Explainer Section */}
        <section className="mt-12 md:mt-16" aria-labelledby="campaigns-explainer-heading">
          <div className="rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-600 to-emerald-600 p-[1px] shadow-xl">
            <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 md:p-10">
              <div className="max-w-4xl">
                <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                Promoter Economy Made Simple
                </span>
                <h2
                  id="campaigns-explainer-heading"
                  className="mt-4 text-2xl md:text-3xl font-bold text-gray-900 dark:text-white"
                >
                  What are campaigns and how do they work?
                </h2>
                <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                  Campaigns are paid content opportunities where brands invite promoters to post videos about their products or services.
                  You choose a campaign you like, create your content, publish on your platform, and earn based on performance.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <Card className="border border-blue-100 dark:border-blue-900/40 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                      <Icon icon="heroicons:light-bulb" className="w-5 h-5" />
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Why it&apos;s great for promoters</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      This is one of the easiest ways to turn your creativity into income. Pick campaigns that match your audience,
                      publish quality content, and build your personal brand while getting paid for your work.
                    </p>
                  </div>
                </Card>

                <Card className="border border-emerald-100 dark:border-emerald-900/40 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                      <Icon icon="heroicons:currency-dollar" className="w-5 h-5" />
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">How you make money</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      You earn <span className="font-semibold text-emerald-600">R20 per 1,000 views</span> on qualifying campaign videos.
                      Top-performing promoters can also earn bonus payouts for delivering the best content and strongest results.
                    </p>
                  </div>
                </Card>

                <Card className="border border-cyan-100 dark:border-cyan-900/40 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-cyan-700 dark:text-cyan-300">
                      <Icon icon="heroicons:rocket-launch" className="w-5 h-5" />
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">How it works in 3 steps</h3>
                    </div>
                    <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300 list-decimal list-inside">
                      <li>Choose a campaign that fits your style and audience.</li>
                      <li>Create and publish your video on supported platforms.</li>
                      <li>Track views and get paid for your performance.</li>
                    </ol>
                  </div>
                </Card>

                <Card className="border border-purple-100 dark:border-purple-900/40 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                      <Icon icon="heroicons:building-office-2" className="w-5 h-5" />
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Why companies love campaigns</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      As a company, getting people to create authentic content for your brand has never been easier.
                      Launch once, reach many promoters fast, and scale awareness with real social proof across multiple platforms.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}